import cx from 'classnames';
import { useDashboardAssignments } from 'components/admin/dashboard/hooks';
import { AssignmentState } from 'components/admin/dashboard/types';
import { Anchor, Button, Card } from 'components/admin/ui';
import { useWhiteLabelContext } from 'components/white-label';
import { CheckCircle } from 'ethena-icons';
import { FE_URL_PATHS } from 'lib/constants';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { Confetti } from '../shared';
import {
  cleanupOnboardingStore,
  getHasViewedConfetti,
  isOnboardingStepComplete,
  setHasViewedConfetti,
} from './helpers';
import { useOnboardingContext } from './hooks';
import css from './OnboardingChecklist.module.scss';
import { OnboardingStep, OnboardingStepStatus } from './types';

interface IOnboardingChecklistItemProps {
  buttonLabel: string;
  description?: string;
  href: string;
  index: number;
  isComplete: boolean;
  isNextStep: boolean;
  title: string;
}

const OnboardingChecklistItem = ({
  buttonLabel,
  description,
  href,
  index,
  isComplete,
  isNextStep,
  title,
}: IOnboardingChecklistItemProps) => {
  const renderAction = () =>
    isNextStep && (
      <div className={css.action}>
        <Anchor href={href} size="sm" variant="button-solid">
          {buttonLabel}
        </Anchor>
      </div>
    );

  const renderCompleteIcon = () =>
    isComplete && <CheckCircle className={css.completeIcon} />;

  const renderDescription = () =>
    !isComplete && <div className={css.description}>{description}</div>;

  const renderTitle = () => (
    <div className={css.title}>
      {index + 1}. {title}
    </div>
  );

  return (
    <li
      className={cx(css.item, {
        [css.active]: isNextStep,
        [css.complete]: isComplete,
      })}
    >
      <div>
        {renderTitle()}
        {renderDescription()}
      </div>
      {renderAction()}
      {renderCompleteIcon()}
    </li>
  );
};

interface IOnboardingChecklistProps {
  className?: string;
  isHorizontal?: boolean;
  reviewSettingsDescription?: string;
  uploadEmployeesDescription?: string;
}

export const OnboardingChecklist = ({
  className,
  isHorizontal = false,
  reviewSettingsDescription,
  uploadEmployeesDescription,
}: IOnboardingChecklistProps) => {
  const {
    areAllStepsComplete,
    completeOnboarding,
    isOnboardingComplete,
    nextOnboardingStep,
    onboardingSteps,
  } = useOnboardingContext();

  const { organizationName } = useWhiteLabelContext();

  const { data: assignments } = useDashboardAssignments(
    [AssignmentState.IN_PROGRESS, AssignmentState.UPCOMING],
    {
      shouldFetch: nextOnboardingStep === OnboardingStep.REVIEW_ASSIGNMENT,
    }
  );

  const [showConfetti, setShowConfetti] = useState<boolean>(false);

  useEffect(() => {
    if (areAllStepsComplete && !getHasViewedConfetti()) {
      setShowConfetti(true);
    }
  }, [areAllStepsComplete]);

  const handleCloseClick = async () => {
    await completeOnboarding();
    cleanupOnboardingStore();
  };

  const getReviewAssignmentHref = () => {
    const assignment = _.first(assignments);
    return `${FE_URL_PATHS.ADMIN_TRAINING_CAMPAIGNS}/${assignment?.key}`;
  };

  const getAddTrainingHref = () => {
    return `${FE_URL_PATHS.ADMIN_TRAINING_CAMPAIGNS}/create`;
  };

  const getTitle = () => {
    const completeStepsCount = _.filter(
      onboardingSteps,
      (s) => s.status === OnboardingStepStatus.COMPLETED
    ).length;
    return `${organizationName} Set up Checklist ${completeStepsCount}/${onboardingSteps.length}`;
  };

  const renderCloseButton = () =>
    !nextOnboardingStep && (
      <Button
        className={css.closeButton}
        onClick={handleCloseClick}
        variant="link"
      >
        Close
      </Button>
    );

  const renderConfetti = () =>
    showConfetti && (
      <Confetti className={css.confetti} onLoad={setHasViewedConfetti} />
    );

  if (_.isEmpty(onboardingSteps) || isOnboardingComplete) {
    return null;
  }

  return (
    <Card className={cx(css.container, className)}>
      <Card.Title className="d-flex justify-content-between align-items-center">
        {getTitle()}
        {renderCloseButton()}
      </Card.Title>
      {renderConfetti()}
      <ol className={cx(css.list, { [css.horizontal]: isHorizontal })}>
        {_.map(onboardingSteps, (step, index) => {
          const isComplete = isOnboardingStepComplete(step);
          const isNextStep = step.type === nextOnboardingStep;
          switch (step.type) {
            case OnboardingStep.REVIEW_SETTINGS:
              return (
                <OnboardingChecklistItem
                  buttonLabel="Review Settings"
                  description={
                    reviewSettingsDescription ??
                    'Review settings, setup reminders, add policy links.'
                  }
                  href="/account"
                  index={index}
                  isComplete={isComplete}
                  isNextStep={isNextStep}
                  key={step.type}
                  title="Review Settings"
                />
              );
            case OnboardingStep.UPLOAD_EMPLOYEES:
              return (
                <OnboardingChecklistItem
                  buttonLabel="Add Members"
                  description={
                    uploadEmployeesDescription ??
                    'Get started by adding your team members.'
                  }
                  href="/team"
                  index={index}
                  isComplete={isComplete}
                  isNextStep={isNextStep}
                  key={step.type}
                  title="Add Team Members"
                />
              );
            case OnboardingStep.SCHEDULE_ASSIGNMENT:
              return (
                <OnboardingChecklistItem
                  buttonLabel="Add Training"
                  description="Choose what goes out — and who sees it."
                  href={getAddTrainingHref()}
                  index={index}
                  isComplete={isComplete}
                  isNextStep={isNextStep}
                  key={step.type}
                  title="Assign Training"
                />
              );
            case OnboardingStep.REVIEW_ASSIGNMENT:
              return (
                <OnboardingChecklistItem
                  buttonLabel="Review Training"
                  description="See how it's going with a dedicated dashboard for your team's progress."
                  href={getReviewAssignmentHref()}
                  index={index}
                  isComplete={isComplete}
                  isNextStep={isNextStep}
                  key={step.type}
                  title="Review Insights"
                />
              );
          }
        })}
      </ol>
    </Card>
  );
};
